import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Hls from "hls.js";
import { Play, Radio, Eye, MoreVertical, Share2, Flag } from "lucide-react";
import { api } from "../api";
import { useGuard } from "../auth";
import { useImageViewer } from "../lightbox";
import { mediaUrl, thumbUrl, fmtNum, fmtDur, timeAgo } from "../config";

const DefaultAvatar = ({ size }) => (
  <div
    className="flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#2a2f36]"
    style={{ width: size, height: size }}
    data-testid="default-avatar"
  >
    <svg viewBox="0 0 24 24" width={size * 0.9} height={size * 0.9} fill="#7d8590" aria-hidden="true">
      <path d="M12 11.5c2.2 0 4-1.8 4-4s-1.8-4-4-4-4 1.8-4 4 1.8 4 4 4zm0 1.5c-2.9 0-8.5 1.5-8.5 4.4V20h17v-2.6c0-2.9-5.6-4.4-8.5-4.4z" />
    </svg>
  </div>
);

export const Avatar = ({ user, size = 36 }) => {
  const openImage = useImageViewer();
  const src = mediaUrl(user?.avatar);
  if (!src) return <DefaultAvatar size={size} />;
  return (
    <img
      src={src}
      alt={user?.displayName || "profile picture"}
      className="shrink-0 cursor-zoom-in rounded-full object-cover"
      style={{ width: size, height: size }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        openImage(src);
      }}
    />
  );
};

export const SectionTitle = ({ children }) => (
  <h2 className="mb-4 font-heading text-lg font-bold tracking-tight text-white sm:text-xl">{children}</h2>
);

const HoverPreview = ({ video }) => {
  const ref = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => () => hlsRef.current?.destroy(), []);

  const start = () => {
    const el = ref.current;
    if (!el) return;
    if (!el.dataset.loaded) {
      el.dataset.loaded = "1";
      const src = mediaUrl(video.webVideoUrl || video.videoUrl);
      if (!src) return;
      if (video.streamType === "hls" || src.includes(".m3u8")) {
        if (el.canPlayType("application/vnd.apple.mpegurl")) el.src = src;
        else if (Hls.isSupported()) {
          const hls = new Hls();
          hls.loadSource(src);
          hls.attachMedia(el);
          hlsRef.current = hls;
        }
      } else {
        el.src = src;
      }
    }
    el.play().catch(() => {});
  };

  const stop = () => {
    const el = ref.current;
    if (el) el.pause();
  };

  return (
    <div onMouseEnter={start} onMouseLeave={stop} className="absolute inset-0">
      <video
        ref={ref}
        muted
        loop
        playsInline
        preload="none"
        poster={thumbUrl(video) || undefined}
        className="h-full w-full object-cover"
      />
    </div>
  );
};

// Shows a real frame from the video when there's no thumbnail image.
const VideoFrameThumb = ({ video, title }) => (
  <video
    muted
    playsInline
    preload="metadata"
    src={`${mediaUrl(video.webVideoUrl || video.videoUrl)}#t=0.1`}
    poster={thumbUrl(video) || undefined}
    aria-label={title}
    className="h-full w-full object-cover"
  />
);

export const VideoCard = ({ video, to, preview = false }) => {
  const guard = useGuard();
  const [menuOpen, setMenuOpen] = useState(false);
  const [note, setNote] = useState("");
  const watchUrl = to || `/app/watch/${video.id}`;

  const flash = (msg) => {
    setNote(msg);
    setTimeout(() => setNote(""), 2000);
  };

  const share = async () => {
    setMenuOpen(false);
    if (!guard()) return;
    const url = `${window.location.origin}/app/watch/${video.id}`;
    try {
      if (navigator.share) await navigator.share({ title: video.title, url });
      else await navigator.clipboard.writeText(url);
      flash("Link copied");
    } catch {}
  };

  const report = async () => {
    setMenuOpen(false);
    if (!guard()) return;
    try {
      await api.post("/reports", { kind: "video", targetId: video.id, ref: video.id, reason: "Reported from the web app" });
    } catch {}
    flash("Reported");
  };

  return (
    <div className="group relative" data-testid={`video-card-${video.id}`}>
      <Link to={watchUrl} className="block">
        <div className="relative aspect-video overflow-hidden rounded-xl bg-surface">
          {preview ? (
            <HoverPreview video={video} />
          ) : thumbUrl(video) ? (
            <img src={thumbUrl(video)} alt={video.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : mediaUrl(video.webVideoUrl || video.videoUrl) ? (
            <VideoFrameThumb video={video} title={video.title} />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Play size={28} className="text-slate-600" />
            </div>
          )}
          {video.durationSec > 0 && (
            <span className="absolute bottom-1.5 right-1.5 rounded-md bg-black/80 px-1.5 py-0.5 font-mono text-[10px] font-medium text-white">
              {fmtDur(video.durationSec)}
            </span>
          )}
        </div>
      </Link>
      <div className="mt-3 flex gap-3">
        <Link to={`/app/creator/${video.creatorId}`} className="shrink-0">
          <Avatar user={video.creator} size={36} />
        </Link>
        <div className="min-w-0 flex-1">
          <Link to={watchUrl}>
            <p className="line-clamp-2 font-heading text-sm font-semibold leading-snug text-white">{video.title}</p>
          </Link>
          <Link to={`/app/creator/${video.creatorId}`} className="mt-1 block truncate text-xs text-slate-400 transition-colors hover:text-white">
            {video.creator?.displayName || video.creator?.username}
          </Link>
          <p className="text-xs text-slate-500">
            {fmtNum(video.views)} views · {timeAgo(video.createdAt)}
          </p>
        </div>
        <div className="relative">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="More options"
            data-testid={`video-menu-${video.id}`}
            className="rounded-full p-1.5 text-slate-400 opacity-0 transition-opacity hover:bg-white/10 hover:text-white focus:opacity-100 group-hover:opacity-100"
          >
            <MoreVertical size={16} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-8 z-20 w-40 overflow-hidden rounded-xl border border-white/10 bg-surface shadow-2xl" data-testid={`video-menu-dropdown-${video.id}`}>
              <button onClick={share} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-white/5" data-testid={`video-share-${video.id}`}>
                <Share2 size={14} /> Share
              </button>
              <button onClick={report} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-white/5" data-testid={`video-report-${video.id}`}>
                <Flag size={14} /> Report
              </button>
            </div>
          )}
        </div>
      </div>
      {note && (
        <span className="absolute left-3 top-3 rounded-full bg-black/80 px-3 py-1 text-xs font-semibold text-white" data-testid="video-card-note">
          {note}
        </span>
      )}
    </div>
  );
};

export const PastStreamCard = ({ stream }) => {
  const [hover, setHover] = useState(false);
  return (
    <div className="group relative" data-testid={`past-stream-${stream.id}`}>
      <Link
        to={`/app/live/${stream.id}`}
        className="block"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div className="relative aspect-video overflow-hidden rounded-xl bg-surface">
          {hover ? (
            <iframe
              src={`https://www.youtube.com/embed/${stream.youtubeId}?autoplay=1&mute=1&controls=0&playsinline=1&rel=0`}
              title={stream.title}
              allow="autoplay; encrypted-media"
              className="pointer-events-none h-full w-full"
              data-testid={`past-stream-preview-${stream.id}`}
            />
          ) : (
            <img
              src={`https://i.ytimg.com/vi/${stream.youtubeId}/hqdefault.jpg`}
              alt={stream.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          )}
          <span className="absolute bottom-1.5 right-1.5 rounded-md bg-black/80 px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-white">
            Past live
          </span>
        </div>
      </Link>
      <div className="mt-3 flex gap-3">
        <Link to={`/app/creator/${stream.creator?.id || stream.creatorId}`} className="shrink-0">
          <Avatar user={stream.creator} size={36} />
        </Link>
        <div className="min-w-0 flex-1">
          <Link to={`/app/live/${stream.id}`}>
            <p className="line-clamp-2 font-heading text-sm font-semibold leading-snug text-white">{stream.title}</p>
          </Link>
          <Link to={`/app/creator/${stream.creator?.id || stream.creatorId}`} className="mt-1 block truncate text-xs text-slate-400 transition-colors hover:text-white">
            {stream.creator?.displayName || stream.creator?.username}
          </Link>
          <p className="text-xs text-slate-500">Streamed {timeAgo(stream.createdAt)}</p>
        </div>
      </div>
    </div>
  );
};

export const ShortCard = ({ video }) => (
  <Link
    to="/app/shorts"
    data-testid={`short-card-${video.id}`}
    className="group relative block w-32 shrink-0 overflow-hidden rounded-2xl border border-white/10 transition-colors duration-300 hover:border-crimson/40 sm:w-36"
  >
    <div className="aspect-[9/16] bg-surface">
      {thumbUrl(video) ? (
        <img src={thumbUrl(video)} alt={video.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      ) : mediaUrl(video.webVideoUrl || video.videoUrl) ? (
        <VideoFrameThumb video={video} title={video.title} />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <Play size={24} className="text-slate-600" />
        </div>
      )}
    </div>
    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-2.5 pt-8 opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-active:opacity-100">
      <p className="truncate text-xs font-semibold text-white">{video.title}</p>
      <p className="font-mono text-[9px] uppercase tracking-wider text-slate-400">{fmtNum(video.views)} views</p>
    </div>
  </Link>
);

export const LiveCard = ({ stream }) => {
  const live = stream.isLive || stream.status === "live";
  return (
    <Link
      to={`/app/live/${stream.id}`}
      data-testid={`live-card-${stream.id}`}
      className="group block w-72 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] transition-colors duration-300 hover:border-crimson/40"
    >
      <div className="relative aspect-video overflow-hidden bg-surface">
        <img
          src={`https://i.ytimg.com/vi/${stream.youtubeId}/hqdefault.jpg`}
          alt={stream.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className={`absolute left-2 top-2 flex items-center gap-1.5 rounded-full px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-widest ${live ? "bg-crimson text-white" : "bg-black/70 text-slate-300"}`}>
          {live && <Radio size={10} />}
          {live ? "Live" : "Ended"}
        </span>
        {live && (
          <span className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 font-mono text-[10px] text-slate-200">
            <Eye size={10} /> {fmtNum(stream.viewers)}
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="truncate font-heading text-sm font-semibold text-white">{stream.title}</p>
        <p className="truncate text-xs text-slate-500">{stream.creator?.displayName || stream.creator?.username}</p>
      </div>
    </Link>
  );
};

export const SeriesCard = ({ series }) => {
  const direct = series.cover || series.coverImage || series.thumbnail;
  const [fallback, setFallback] = useState("");

  useEffect(() => {
    if (direct) return;
    api
      .get(`/series/${series.id}`)
      .then((r) => {
        const eps = r.episodes ?? r.series?.episodes ?? [];
        if (eps[0]?.thumbnail) setFallback(eps[0].thumbnail);
      })
      .catch(() => {});
  }, [series.id, direct]);

  const img = mediaUrl(direct || fallback);

  return (
    <Link
      to={`/app/series/${series.id}`}
      data-testid={`series-card-${series.id}`}
      className="group block w-44 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] transition-colors duration-300 hover:border-crimson/40 sm:w-48"
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-surface">
        {img ? (
          <img src={img} alt={series.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-heading text-3xl font-black text-slate-700">
            {series.title?.[0]}
          </div>
        )}
        {series.episodeCount > 0 && (
          <span className="absolute bottom-2 right-2 rounded-md bg-black/80 px-1.5 py-0.5 font-mono text-[10px] text-white">
            {series.episodeCount} ep{series.episodeCount === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <div className="p-3">
        <p className="truncate font-heading text-sm font-semibold text-white">{series.title}</p>
        <p className="truncate text-xs text-slate-500">{series.creator?.displayName || series.genre || ""}</p>
      </div>
    </Link>
  );
};

export const Empty = ({ children }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-6 py-12 text-center text-sm text-slate-500" data-testid="empty-state">
    {children}
  </div>
);

export const Spinner = () => (
  <div className="flex flex-col items-center justify-center gap-4 py-16" data-testid="loading-spinner">
    <img src="/brand.jpg" alt="Michael C Streamline" className="h-14 w-14 rounded-2xl object-cover" />
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/10 border-t-crimson" />
  </div>
);
